import React, { useState, useEffect, useMemo } from 'react';
import workOrderService from '../api/workOrderService';
import projectService from '../api/projectService';
// ─── Paleta institucional Elecom — tokens compartidos (ver src/theme/colors.js) ──
import { COLORS as C } from '../theme/colors';
// ─── Escala semántica de estado — fuente única de verdad (ver src/theme/status.js) ──
import { STATUS_TONES, ORDER_STATUS_TONE } from '../theme/status';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08) — Dashboard
// es la fuente del patrón, pero consume el componente igual que el resto de páginas.
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';

// ─── Constantes de dominio ────────────────────────────────────────────────────

const ORDER_STATUSES = ['PENDING', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];

const STATUS_LABELS = {
  PENDING:     'Pendiente',
  ASSIGNED:    'Asignada',
  IN_PROGRESS: 'En Progreso',
  COMPLETED:   'Completada',
  CANCELED:    'Cancelada',
};

/**
 * Color del indicador lateral por estado, derivado de la misma escala semántica
 * que usan los badges de WorkOrders.js y MaintenanceOrders.js (theme/status.js),
 * para que el mismo estado se lea siempre con el mismo color en toda la app.
 */
const STATUS_COLOR = Object.fromEntries(
  ORDER_STATUSES.map((status) => [status, STATUS_TONES[ORDER_STATUS_TONE[status]].color])
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const isToday = (isoString) => {
  if (!isoString) return false;
  const d = new Date(isoString);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() &&
    d.getMonth() === t.getMonth() &&
    d.getDate() === t.getDate();
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

/**
 * @description Tarjeta de KPI con acento de color institucional.
 */
const KpiCard = ({ label, value, icon, accent = C.teal }) => (
  <div style={{ ...styles.kpiCard, borderLeft: `4px solid ${accent}` }}>
    <div style={styles.kpiIcon}>{icon}</div>
    <div style={styles.kpiValue}>{value}</div>
    <div style={styles.kpiLabel}>{label}</div>
  </div>
);

/**
 * @description Encabezado de sección con subrayado teal.
 */
const SectionTitle = ({ children }) => (
  <h3 style={styles.sectionTitle}>{children}</h3>
);

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * @description Página principal del panel. Muestra KPIs, distribución por estado,
 * OTs de alta prioridad sin asignar y proyectos activos.
 */
const Dashboard = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [projects, setProjects]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // Carga inicial: ambas APIs en paralelo
  useEffect(() => {
    const loadData = async () => {
      try {
        const [ordersRes, projectsRes] = await Promise.all([
          workOrderService.getWorkOrders({ limit: 50 }),
          projectService.getProjects({ limit: 50 }),
        ]);
        setWorkOrders(ordersRes.data.data);
        setProjects(projectsRes.data.data);
      } catch (err) {
        console.error('Error cargando datos del dashboard:', err);
        setError('No se pudieron cargar los datos. Verifique la conexión con el servidor.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ── Cálculos derivados ────────────────────────────────────────────────────

  const totalOrders        = useMemo(() => workOrders.length, [workOrders]);
  const pendingOrAssigned  = useMemo(
    () => workOrders.filter((o) => o.status === 'PENDING' || o.status === 'ASSIGNED').length,
    [workOrders]
  );
  const inProgress         = useMemo(
    () => workOrders.filter((o) => o.status === 'IN_PROGRESS').length,
    [workOrders]
  );
  const completedToday     = useMemo(
    () => workOrders.filter((o) => o.status === 'COMPLETED' && isToday(o.closed_at)).length,
    [workOrders]
  );

  const statusDistribution = useMemo(() => {
    const total = workOrders.length || 1;
    return ORDER_STATUSES.map((status) => {
      const count = workOrders.filter((o) => o.status === status).length;
      return { status, label: STATUS_LABELS[status], count, percentage: ((count / total) * 100).toFixed(1) };
    });
  }, [workOrders]);

  const highPriorityUnassigned = useMemo(
    () => workOrders
      .filter((o) => o.priority === 'HIGH' && o.status === 'PENDING')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .slice(0, 10),
    [workOrders]
  );

  const activeProjectsWithCount = useMemo(() => {
    return projects
      .filter((p) => p.status === 'active')
      .map((project) => ({
        ...project,
        workOrderCount: workOrders.filter(
          (o) => String(o.project?.id) === String(project.project_id)
        ).length,
      }));
  }, [projects, workOrders]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return <LoadingState message="Cargando dashboard..." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div>
      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <section style={styles.section}>
        <SectionTitle>Resumen general</SectionTitle>
        <div style={styles.kpiRow}>
          <KpiCard label="Total OTs"              value={totalOrders}       icon="📋" accent={C.darkTeal} />
          <KpiCard label="Pendientes / Asignadas" value={pendingOrAssigned} icon="⏳" accent={pendingOrAssigned > 0 ? STATUS_TONES.warning.color : C.teal} />
          <KpiCard label="En Progreso"            value={inProgress}        icon="⚙️" accent={C.teal} />
          <KpiCard label="Completadas hoy"        value={completedToday}    icon="✅" accent={C.darkTeal} />
        </div>
      </section>

      {/* ── Distribución por estado ───────────────────────────────────── */}
      <section style={styles.section}>
        <SectionTitle>Distribución por estado</SectionTitle>
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={styles.th}>Estado</th>
                <th style={{ ...styles.th, width: '140px' }}>Cantidad</th>
                <th style={{ ...styles.th, width: '80px' }}>%</th>
                <th style={{ ...styles.th }}>Barra</th>
              </tr>
            </thead>
            <tbody>
              {statusDistribution.map(({ status, label, count, percentage }) => (
                <tr key={status} style={styles.tableRow}>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusDot, backgroundColor: STATUS_COLOR[status] }} />
                    {label}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>{count}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: 'rgba(56,62,65,0.5)' }}>{percentage}%</td>
                  <td style={styles.td}>
                    <div style={styles.barTrack}>
                      <div style={{
                        ...styles.barFill,
                        width: `${percentage}%`,
                        backgroundColor: STATUS_COLOR[status],
                      }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Alta prioridad sin asignar ────────────────────────────────── */}
      <section style={styles.section}>
        <SectionTitle>OTs de alta prioridad sin asignar</SectionTitle>
        {highPriorityUnassigned.length === 0 ? (
          <p style={styles.emptyMessage}>No hay órdenes de alta prioridad pendientes. ✓</p>
        ) : (
          <div style={styles.priorityList}>
            {highPriorityUnassigned.map((order) => (
              <div key={order.work_order_id ?? order.id} style={styles.priorityItem}>
                <div style={styles.priorityAccent} />
                <div style={{ flex: 1 }}>
                  <div style={styles.priorityTitle}>{order.title}</div>
                  <div style={styles.prioritySub}>{order.project?.name ?? 'Sin proyecto'}</div>
                </div>
                <div style={styles.priorityDate}>{formatDate(order.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Proyectos activos ────────────────────────────────────────── */}
      <section style={styles.section}>
        <SectionTitle>Proyectos activos</SectionTitle>
        {activeProjectsWithCount.length === 0 ? (
          <p style={styles.emptyMessage}>No hay proyectos activos.</p>
        ) : (
          <div style={styles.projectGrid}>
            {activeProjectsWithCount.map((project) => (
              <div key={project.project_id} style={styles.projectCard}>
                <div style={styles.projectCardTop}>
                  <div style={styles.projectIcon}>P</div>
                  <span style={styles.projectBadge}>
                    {project.workOrderCount} {project.workOrderCount === 1 ? 'OT' : 'OTs'}
                  </span>
                </div>
                <div style={styles.projectName}>{project.name}</div>
                <div style={styles.projectClient}>{project.client_name}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// ─── Estilos ──────────────────────────────────────────────────────────────────

const styles = {
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: C.teal,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: `1px solid rgba(26,184,171,0.2)`,
  },

  // KPIs
  kpiRow: {
    display: 'flex',
    gap: '14px',
    flexWrap: 'wrap',
  },
  kpiCard: {
    flex: '1 1 160px',
    backgroundColor: C.white,
    borderRadius: '10px',
    padding: '20px 20px 18px',
    boxShadow: '0 1px 4px rgba(56,62,65,0.08)',
    border: `1px solid rgba(56,62,65,0.08)`,
  },
  kpiIcon: {
    fontSize: '20px',
    marginBottom: '10px',
  },
  kpiValue: {
    fontSize: '34px',
    fontWeight: '700',
    color: C.charcoal,
    lineHeight: '1',
    marginBottom: '6px',
  },
  kpiLabel: {
    fontSize: '12px',
    color: `rgba(56,62,65,0.55)`,
    fontWeight: '500',
  },

  // Tabla distribución
  card: {
    backgroundColor: C.white,
    borderRadius: '10px',
    border: `1px solid rgba(56,62,65,0.08)`,
    boxShadow: '0 1px 4px rgba(56,62,65,0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeaderRow: {
    backgroundColor: `rgba(56,62,65,0.03)`,
    borderBottom: `1px solid rgba(56,62,65,0.08)`,
  },
  tableRow: {
    borderBottom: `1px solid rgba(56,62,65,0.05)`,
  },
  th: {
    textAlign: 'left',
    padding: '11px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: `rgba(56,62,65,0.55)`,
    letterSpacing: '0.4px',
  },
  td: {
    padding: '12px 16px',
    color: C.charcoal,
    verticalAlign: 'middle',
  },
  statusDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
  barTrack: {
    height: '6px',
    backgroundColor: `rgba(56,62,65,0.08)`,
    borderRadius: '3px',
    overflow: 'hidden',
    minWidth: '80px',
  },
  barFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.4s ease',
  },

  // Alta prioridad
  priorityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  priorityItem: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: C.white,
    borderRadius: '8px',
    border: `1px solid rgba(56,62,65,0.08)`,
    boxShadow: '0 1px 3px rgba(56,62,65,0.06)',
    padding: '12px 16px',
    gap: '14px',
    fontSize: '14px',
  },
  priorityAccent: {
    width: '3px',
    height: '36px',
    // Mismo naranja "warning" que IN_PROGRESS en STATUS_TONES — antes hardcodeado suelto
    backgroundColor: STATUS_TONES.warning.color,
    borderRadius: '2px',
    flexShrink: 0,
  },
  priorityTitle: {
    fontWeight: '600',
    color: C.charcoal,
    marginBottom: '2px',
  },
  prioritySub: {
    fontSize: '12px',
    color: `rgba(56,62,65,0.5)`,
  },
  priorityDate: {
    fontSize: '12px',
    color: `rgba(56,62,65,0.4)`,
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },

  // Proyectos
  projectGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  projectCard: {
    flex: '1 1 200px',
    backgroundColor: C.white,
    border: `1px solid rgba(56,62,65,0.08)`,
    borderRadius: '10px',
    padding: '18px',
    boxShadow: '0 1px 4px rgba(56,62,65,0.08)',
  },
  projectCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  projectIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: `rgba(26,184,171,0.1)`,
    color: C.darkTeal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
  },
  projectBadge: {
    fontSize: '12px',
    fontWeight: '600',
    color: C.darkTeal,
    backgroundColor: `rgba(26,184,171,0.1)`,
    padding: '3px 10px',
    borderRadius: '12px',
  },
  projectName: {
    fontWeight: '600',
    fontSize: '15px',
    color: C.charcoal,
    marginBottom: '4px',
  },
  projectClient: {
    fontSize: '13px',
    color: `rgba(56,62,65,0.5)`,
  },

  // Estados generales — loading/error ahora viven en LoadingState/ErrorState
  // (src/components/), ver hallazgo de auditoría #08.
  emptyMessage: {
    color: `rgba(56,62,65,0.45)`,
    fontSize: '14px',
    fontStyle: 'italic',
    padding: '8px 0',
  },
};

export default Dashboard;
